import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, MapPin, MessageCircle, CheckCircle2, Loader2, Mail } from "lucide-react";
import { Reveal, SectionHead, EASE } from "./Reveal";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, waLink } from "../lib";
import { SEGMENTS } from "../data/products";

const INTERESTS = [
    ...SEGMENTS.map((s) => s.name),
    "Cleanroom & Modular OT (Turnkey Project)",
    "CSSD (Design & Build)",
    "Custom / OEM Sourcing",
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full rounded-xl border border-line bg-ink/60 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-accent/60 focus:ring-1 focus:ring-accent/40";

export default function Enquiry() {
    const [interests, setInterests] = useState([]);
    const [status, setStatus] = useState("idle");
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        hospital: "",
        message: "",
    });

    useEffect(() => {
        const onEnquire = (e) => {
            setStatus("idle");
            const { interest, product } = e.detail || {};
            if (interest) {
                setInterests((prev) =>
                    prev.includes(interest) ? prev : [...prev, interest],
                );
            }
            if (product) {
                setForm((f) => ({
                    ...f,
                    message: f.message || `Enquiry about: ${product}`,
                }));
            }
        };
        window.addEventListener("ss:enquire-product", onEnquire);
        return () =>
            window.removeEventListener("ss:enquire-product", onEnquire);
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const toggleInterest = (i) =>
        setInterests((prev) =>
            prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i],
        );

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) return;
        try {
            setStatus("sending");
            await axios.post(`${API}/enquiries`, {
                ...form,
                interests,
            });
            setStatus("done");
        } catch (err) {
            console.error(err);
            setStatus("error");
        }
    };

    const reset = () => {
        setForm({ name: "", phone: "", email: "", hospital: "", message: "" });
        setInterests([]);
        setStatus("idle");
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-24 lg:py-32"
        >
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[820px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/5 blur-[160px]" />
            <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="overflow-hidden rounded-3xl border border-line bg-panel/40 p-7 sm:p-10 lg:p-14">
                    <div className="grid gap-14 lg:grid-cols-2">
                        <div>
                            <SectionHead
                                label="Start a Conversation"
                                title="Tell us what you're building."
                            />
                            <Reveal delay={0.15}>
                                <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
                                    A new theatre, a full CSSD, or a single
                                    bed — send us the requirement and our team
                                    will respond with a tailored proposal.
                                </p>
                            </Reveal>

                            <div className="mt-10 space-y-5">
                                <Reveal delay={0.2}>
                                    <a
                                        data-testid="contact-phone-row"
                                        href={PHONE_TEL}
                                        className="group flex items-center gap-4"
                                    >
                                        <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-ink/60 text-accent transition-colors group-hover:border-accent/50">
                                            <Phone className="h-4 w-4" />
                                        </span>
                                        <div>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                                Call us
                                            </p>
                                            <p className="mt-0.5 font-display font-bold tracking-wide text-white">
                                                {PHONE_DISPLAY}
                                            </p>
                                        </div>
                                    </a>
                                </Reveal>
                                <Reveal delay={0.26}>
                                    <a
                                        data-testid="contact-whatsapp-row"
                                        href={waLink(
                                            "Hello Science & Surgical, I'd like to discuss a requirement.",
                                        )}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="group flex items-center gap-4"
                                    >
                                        <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-ink/60 text-wa transition-colors group-hover:border-wa/50">
                                            <MessageCircle className="h-4 w-4" />
                                        </span>
                                        <div>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                                WhatsApp
                                            </p>
                                            <p className="mt-0.5 font-display font-bold tracking-wide text-white">
                                                Chat with our team
                                            </p>
                                        </div>
                                    </a>
                                </Reveal>
                                <Reveal delay={0.3}>
                                    <a
                                        data-testid="contact-email-row"
                                        href={`mailto:${EMAIL}`}
                                        className="group flex items-center gap-4"
                                    >
                                        <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-ink/60 text-accent transition-colors group-hover:border-accent/50">
                                            <Mail className="h-4 w-4" />
                                        </span>
                                        <div>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                                Email
                                            </p>
                                            <p className="mt-0.5 font-display font-bold tracking-wide text-white">
                                                {EMAIL}
                                            </p>
                                        </div>
                                    </a>
                                </Reveal>
                                <Reveal delay={0.34}>
                                    <div className="flex items-center gap-4">
                                        <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-ink/60 text-accent">
                                            <MapPin className="h-4 w-4" />
                                        </span>
                                        <div>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                                Head Office
                                            </p>
                                            <p className="mt-0.5 font-display font-bold tracking-wide text-white">
                                                300, Roy Bahadur Road,
                                                <br />
                                                Kolkata, West Bengal - 700053
                                            </p>
                                        </div>
                                    </div>
                                </Reveal>
                            </div>

                            <Reveal delay={0.42}>
                                <div className="mt-6 overflow-hidden rounded-2xl border border-line">
                                    <iframe
                                        data-testid="head-office-map"
                                        title="Science & Surgical Head Office — 300, Roy Bahadur Road, Kolkata"
                                        src="https://maps.google.com/maps?q=300%20Roy%20Bahadur%20Road%2C%20Kolkata%2C%20West%20Bengal%20700053&z=15&output=embed"
                                        className="map-dark h-56 w-full"
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                    />
                                </div>
                            </Reveal>

                            <Reveal delay={0.38}>
                                <p
                                    data-testid="contact-registration-line"
                                    className="mt-10 border-t border-line pt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600"
                                >
                                    GST 19ADRFS8199Q1ZF · PAN ADRFS8199Q · IEC
                                    ADRFS8199Q
                                </p>
                            </Reveal>
                        </div>

                        <div>
                            {status === "done" ? (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.96 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5, ease: EASE }}
                                    data-testid="enquiry-success-panel"
                                    className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-2xl border border-mint/30 bg-mint/5 p-8 text-center"
                                >
                                    <CheckCircle2 className="h-12 w-12 text-mint" />
                                    <h3 className="mt-6 font-display text-2xl font-bold text-white">
                                        Enquiry received.
                                    </h3>
                                    <p className="mt-3 max-w-xs text-sm leading-relaxed text-slate-400">
                                        Thank you — our team will get back to
                                        you shortly. For anything urgent, call
                                        or WhatsApp us directly.
                                    </p>
                                    <button
                                        data-testid="enquiry-reset-button"
                                        onClick={reset}
                                        className="mt-8 rounded-full border border-line px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors hover:border-accent hover:text-accent"
                                    >
                                        Send another enquiry
                                    </button>
                                </motion.div>
                            ) : (
                                <form
                                    onSubmit={onSubmit}
                                    data-testid="enquiry-form"
                                    className="space-y-4"
                                >
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <div>
                                            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                                                Full Name *
                                            </label>
                                            <input
                                                data-testid="enquiry-input-name"
                                                className={inputCls}
                                                value={form.name}
                                                onChange={set("name")}
                                                placeholder="Dr. A. Sharma"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                                                Phone / WhatsApp *
                                            </label>
                                            <input
                                                data-testid="enquiry-input-phone"
                                                className={inputCls}
                                                value={form.phone}
                                                onChange={set("phone")}
                                                placeholder="+91 98xxx xxxxx"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <div>
                                            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                                                Email
                                            </label>
                                            <input
                                                data-testid="enquiry-input-email"
                                                type="email"
                                                className={inputCls}
                                                value={form.email}
                                                onChange={set("email")}
                                                placeholder="you@hospital.org"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                                                Hospital / Facility
                                            </label>
                                            <input
                                                data-testid="enquiry-input-hospital"
                                                className={inputCls}
                                                value={form.hospital}
                                                onChange={set("hospital")}
                                                placeholder="City Hospital"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                                            Product Interest
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {INTERESTS.map((i) => (
                                                <button
                                                    key={i}
                                                    type="button"
                                                    data-testid={`enquiry-interest-pill-${i
                                                        .toLowerCase()
                                                        .replace(/[^a-z0-9]+/g, "-")}`}
                                                    onClick={() =>
                                                        toggleInterest(i)
                                                    }
                                                    className={`rounded-full border px-4 py-2 text-xs tracking-wide transition-all duration-300 ${
                                                        interests.includes(i)
                                                            ? "border-accent bg-accent/10 text-accent"
                                                            : "border-line text-slate-400 hover:border-slate-500 hover:text-white"
                                                    }`}
                                                >
                                                    {i}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                                            Requirements
                                        </label>
                                        <textarea
                                            data-testid="enquiry-input-message"
                                            rows={4}
                                            className={`${inputCls} resize-none`}
                                            value={form.message}
                                            onChange={set("message")}
                                            placeholder="Tell us about the space, the departments, the timeline…"
                                        />
                                    </div>

                                    {status === "error" && (
                                        <p
                                            data-testid="enquiry-form-error"
                                            className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-red-300"
                                        >
                                            Something went wrong — please call
                                            or WhatsApp us instead.
                                        </p>
                                    )}

                                    <button
                                        data-testid="enquiry-form-submit-button"
                                        type="submit"
                                        disabled={status === "sending"}
                                        className="group flex w-full items-center justify-center gap-2 rounded-full bg-accent py-4 font-display text-sm font-bold uppercase tracking-wider text-ink transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,240,255,0.3)] disabled:opacity-60"
                                    >
                                        {status === "sending" ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Sending…
                                            </>
                                        ) : (
                                            "Submit Enquiry"
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
